"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const Union_1 = require("../Union");
const union_1 = __importDefault(require("../Union/union"));
const functions_1 = require("../_internals/functions");
const symbols_1 = require("../_internals/symbols");
const types_1 = require("./types");
const thenableOf = (thenImpl) => ({
    then: thenImpl,
    catch(rej) {
        this.then(undefined, rej);
    }
});
const AsyncType = () => (cases, globals) => {
    cases.Success.prototype.zipWith = function (right, fn) {
        return this.chain(a => right.map(b => fn(a, b)));
    };
    cases.Success.prototype.zip = function (right) {
        return this.zipWith(right, functions_1.makeTuple);
    };
    cases.Success.prototype.zipLeft = function (right) {
        return this.chain((a) => right.mapTo(a));
    };
    cases.Success.prototype.zipRight = function (right) {
        return this.chain(() => right);
    };
    cases.Success.prototype.provide = function (p) {
        const ret = new cases.Success(() => { });
        const cpy = Object.assign({}, this.get());
        let inner = undefined;
        if ((0, functions_1.isPrimitive)(p)) {
            inner = (0, types_1.setEnv)(() => p)(cpy);
        }
        else {
            const currentEnv = (0, types_1.getEnv)(cpy);
            inner = (0, types_1.setEnv)((e) => {
                const prev = currentEnv(e);
                if ((0, functions_1.isPrimitive)(prev)) {
                    return p;
                }
                return Object.assign(Object.assign({}, prev), p);
            })(cpy);
        }
        return (0, symbols_1.setInnerValue)(inner)(ret);
    };
    cases.Success.prototype.providePartial = function (p) {
        return this.provide(p);
    };
    cases.Success.prototype.provideSlice = function (p) {
        return this.provide(p);
    };
    cases.Success.prototype.recover = function (fn) {
        const cpy = this.map(functions_1.identity);
        return (0, symbols_1.setInnerValue)((0, types_1.setCritical)(fn)(cpy.get()))(cpy);
    };
    cases.Success.prototype.ignore = function () {
        const a = this.mapTo(undefined);
        return (0, symbols_1.setInnerValue)((0, types_1.setIgnore)(true)((0, symbols_1.getInnerValue)(a)))(a);
    };
    cases.Success.prototype.provideTo = function (other) {
        return this.chain(a => other.provide(a));
    };
    cases.Success.prototype.provideSliceTo = function (other) {
        return this.chain(a => other.provideSlice(a));
    };
    cases.Success.prototype.providePartialTo = function (other) {
        return this.chain(a => other.providePartial(a));
    };
    cases.Success.prototype.access = function (key) {
        return this.map(({ [key]: attr }) => attr);
    };
    cases.Success.prototype.alias = function (og, aliased) {
        return this.map((data) => (Object.assign(Object.assign({}, data), { [aliased]: data[og] })));
    };
    cases.Success.prototype.rename = function (og, aliased) {
        /* istanbul ignore next */
        return this.map((_a) => {
            var _b = og, val = _a[_b], rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
            return (Object.assign(Object.assign({}, rest), { [aliased]: val }));
        });
    };
    cases.Success.prototype.tapEffect = function (fn) {
        return this.chain((a) => fn(a).mapTo(a));
    };
    cases.Success.prototype.continueIf = function (fn) {
        return this.tapEffect((a) => fn(a) ? new cases.Success(undefined) : new cases.Fail(a));
    };
    cases.Fail.prototype.recover = function (fn) {
        return new cases.Success((0, types_1.getFailure)((0, symbols_1.getInnerValue)(this))).chain(fn);
    };
    cases.Fail.prototype.ignore = function () {
        return new cases.Success(undefined);
    };
    cases.Fail.prototype.zipWith = functions_1.pass;
    cases.Fail.prototype.zip = functions_1.pass;
    cases.Fail.prototype.zipLeft = functions_1.pass;
    cases.Fail.prototype.zipRight = functions_1.pass;
    cases.Fail.prototype.provide = functions_1.pass;
    cases.Fail.prototype.providePartial = functions_1.pass;
    cases.Fail.prototype.provideSlice = functions_1.pass;
    cases.Fail.prototype.provideTo = functions_1.pass;
    cases.Fail.prototype.providePartialTo = functions_1.pass;
    cases.Fail.prototype.provideSliceTo = functions_1.pass;
    cases.Fail.prototype.access = functions_1.pass;
    cases.Fail.prototype.alias = functions_1.pass;
    cases.Fail.prototype.tapEffect = functions_1.pass;
    cases.Fail.prototype.continueIf = functions_1.pass;
    globals.all = function (actions) {
        return new cases.Success(() => [])
            .chain((data) => {
            return actions
                .map(x => x.tap(x => data.push(x)))
                .reduce((acc, next) => acc.chain(() => next))
                .map(() => data);
        });
    };
};
const AsyncDefs = {
    trivials: ["Success"],
    identities: ["Fail"],
    pure: "Success",
    lazy: true,
    overrides: {
        fmap: {
            Success(fn) {
                return Async.Success((env) => (0, types_1.getSuccess)(this.get())(env).then(fn));
            },
        },
        apply: {
            Success(asyncFn) {
                return this.chain(a => asyncFn.map(fn => fn(a)));
            }
        },
        chain: {
            Success(fn) {
                return Async.Success((env) => this.run(env).then(a => fn(a).run(env)));
            },
        },
        join: {
            Success() {
                return this.chain(functions_1.identity);
            }
        },
        show: {
            Success() {
                return `[Async => Success => (R -> _)]`;
            },
            Fail() {
                return `[Async => Fail => (R -> _)]`;
            },
        },
        run: {
            Success(env = {}) {
                return __awaiter(this, void 0, void 0, function* () {
                    const inner = this.get();
                    const provision = (0, types_1.getEnv)(inner);
                    const success = (0, types_1.getSuccess)(inner);
                    const critical = (0, types_1.getCritical)(inner);
                    const ignore = (0, types_1.getIgnore)(inner);
                    try {
                        return yield success(provision(env));
                    }
                    catch (e) {
                        if (ignore) {
                            return undefined;
                        }
                        if (critical) {
                            return critical(e).run();
                        }
                        return yield Promise.reject(e);
                    }
                });
            },
            Fail() {
                return Promise.reject((0, types_1.getFailure)(this.get()));
            }
        },
        traverse(data, fn) {
            const res = [];
            return data
                .map(x => fn(x).tap(x => res.push(x)))
                .reduce((acc, next) => acc.chain(() => next))
                .fmap(() => res);
        },
        toPromise: {
            Success(...args) { return this.run(...args); },
            Fail(...args) { return this.run(...args); },
        },
        toThenable: {
            Success(...args) {
                return thenableOf((res, rej = functions_1.identity) => this.run(...args).then(res, rej));
            },
            Fail(...args) {
                return thenableOf((res, rej) => this.run(...args).then(res, rej));
            }
        }
    },
};
const wrapSuccess = (x) => (env) => Promise.resolve(env).then((0, functions_1.isFunction)(x) ? x : () => x);
const Async = (0, union_1.default)("Async", {
    Success: x => (0, types_1.makeWrapper)(wrapSuccess(x), undefined),
    Fail: x => (0, types_1.makeWrapper)(functions_1.identity, x),
}, [
    (0, Union_1.Functor)(AsyncDefs),
    (0, Union_1.Applicative)(AsyncDefs),
    (0, Union_1.Monad)(AsyncDefs),
    (0, Union_1.Tap)(AsyncDefs),
    (0, Union_1.Runnable)(AsyncDefs),
    (0, Union_1.Show)(AsyncDefs),
    (0, Union_1.Traversable)(AsyncDefs),
    (0, Union_1.Thenable)(AsyncDefs),
    AsyncType()
]).constructors({
    of(fn) {
        return this.Success((r) => Promise.resolve().then(() => fn(r)));
    },
    from(fn) {
        return this.Success(fn);
    },
    fromPromise(p) {
        return this.Success(() => p);
    },
    unary(fn) {
        return (a) => this.Success(() => Promise.resolve(fn(a)));
    },
    identity() {
        return this.Success((a) => Promise.resolve(a));
    },
    through(fn) {
        return (...a) => this.Success(() => Promise.resolve(fn(...a)));
    },
    require() {
        return this.Success((env) => Promise.resolve(env));
    },
    unit() {
        return this.Success(() => Promise.resolve(undefined));
    },
    fromMaybe(m) {
        return m.match({
            Just: this.Success,
            None: this.Fail
        });
    },
    fromEither(e) {
        return e.match({
            Right: this.Success,
            Left: this.Fail
        });
    },
    fromPredicate(fn, a) {
        return fn(a) ? this.Success(() => Promise.resolve(a)) : this.Fail(a);
    },
    fromCondition(fn, a) {
        return fn(a) ? this.Success(() => Promise.resolve(a)) : this.Fail(a);
    },
    fromCallback(fn) {
        return this.Success(() => new Promise(fn));
    }
});
exports.default = Async;
